import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { IndianRupee, ShoppingBag, Users, AlertTriangle } from "lucide-react";
import api from "@/api";
import { formatINR } from "@/store/StoreContext";

function Stat({ label, value, sub, testid }) {
  return (
    <div className="bg-white border border-[#E5DEC9] p-5" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-[0.2em] text-[#8C7A5E]">{label}</p>
      <p className="font-serif text-3xl mt-2">{value}</p>
      {sub && <p className="text-xs text-[#6E6D6A] mt-1">{sub}</p>}
    </div>
  );
}

export default function Dashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then(({ data }) => setD(data)).catch(() => {}); }, []);
  if (!d) return <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{[...Array(4)].map((_, i) => <div key={i} className="h-28 bg-white animate-pulse" />)}</div>;
  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-serif text-3xl mb-6">Dashboard</h1>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Today's Sales" value={formatINR(d.today.total)} sub={`${d.today.count} orders`} testid="stat-today" />
        <Stat label="Yesterday" value={formatINR(d.yesterday.total)} sub={`${d.yesterday.count} orders`} testid="stat-yesterday" />
        <Stat label="This Month" value={formatINR(d.month.total)} sub={`${d.month.count} orders`} testid="stat-month" />
        <Stat label="Avg. Order Value" value={formatINR(d.aov)} sub={`${d.customers} registered customers`} testid="stat-aov" />
      </div>
      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        <div className="lg:col-span-2 bg-white border border-[#E5DEC9] p-5">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8C7A5E] mb-4">Sales — Last 14 Days</p>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={d.graph}>
              <CartesianGrid stroke="#F4EFE6" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#9a938a" />
              <YAxis tick={{ fontSize: 10 }} stroke="#9a938a" tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => formatINR(v)} />
              <Line type="monotone" dataKey="total" stroke="#D4AF37" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-[#E5DEC9] p-5">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8C7A5E] mb-4">Order Status</p>
          <ul className="space-y-2.5 text-sm">
            {Object.entries(d.status_summary).map(([k, v]) => (
              <li key={k} className="flex justify-between"><span className="capitalize text-[#6E6D6A]">{k}</span><span className="font-semibold">{v}</span></li>
            ))}
          </ul>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8C7A5E] mt-6 mb-3">Payments</p>
          <ul className="space-y-2 text-sm">
            {d.pay_summary.map((p) => <li key={p._id} className="flex justify-between"><span className="uppercase text-[#6E6D6A]">{p._id}</span><span className="font-semibold">{p.count}</span></li>)}
          </ul>
        </div>
      </div>
      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        <div className="bg-white border border-[#E5DEC9] p-5">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8C7A5E] mb-4">Top Products</p>
          {d.top_products.length === 0 ? <p className="text-sm text-[#9a938a]">No sales yet.</p> : (
            <ul className="space-y-3 text-sm">
              {d.top_products.map((p) => <li key={p._id} className="flex justify-between gap-2"><span className="truncate">{p._id}</span><span className="shrink-0 font-semibold">{p.qty} sold</span></li>)}
            </ul>
          )}
        </div>
        <div className="bg-white border border-[#E5DEC9] p-5">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8B1E21] mb-4 flex items-center gap-2"><AlertTriangle size={14} /> Low Stock</p>
          {d.low_stock.length === 0 ? <p className="text-sm text-[#9a938a]">All variants healthy.</p> : (
            <ul className="space-y-2 text-sm">
              {d.low_stock.slice(0, 8).map((l, i) => <li key={i} className="flex justify-between gap-2"><span className="truncate">{l.product} · {l.size}</span><span className={`shrink-0 font-semibold ${l.stock === 0 ? "text-[#8B1E21]" : "text-[#C5A059]"}`}>{l.stock} left</span></li>)}
            </ul>
          )}
          <Link to="/admin/inventory" className="text-xs text-[#C5A059] underline mt-4 inline-block">Manage inventory →</Link>
        </div>
        <div className="bg-white border border-[#E5DEC9] p-5">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8C7A5E] mb-4">Recent Orders</p>
          {d.recent_orders.length === 0 ? <p className="text-sm text-[#9a938a]">No orders yet.</p> : (
            <ul className="space-y-3 text-sm">
              {d.recent_orders.map((o) => (
                <li key={o.order_number}>
                  <Link to={`/admin/orders?q=${o.order_number}`} className="flex justify-between gap-2 hover:text-[#C5A059]">
                    <span>#{o.order_number}<span className="text-[#9a938a]"> · {o.customer.name}</span></span>
                    <span className="font-semibold shrink-0">{formatINR(o.total)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
